import { defineComponent, onMounted, ref, Ref } from "vue";
import { Rectangle, Point } from "./quadtree.js";

import {
    useEventListener,
    useEventListenerAutoSwitch,
} from "../utils/hooks.js";

export default defineComponent({

    props: {
        refCanvas: {
            type: Object as () => Ref<HTMLCanvasElement | void>,
            required: true,
        },
        refScale: {
            type: Object as () => Ref<number>,
            required: true,
        },
        viewport: {
            type: Object as () => Rectangle,
            required: true,
        },
        onRender: {
            type: Function as unknown as () => () => void,
            required: true,
        },
        onUpdate: {
            type: Function as unknown as () => (mouseX: number, mouseY: number) => void,
            required: true,
        },
    },

    setup(props) {
        const refIsDragging = ref(false);
        const refDragStart  = ref<Point>({ x: 0, y: 0 });

        function updateCanvasSize() {
            const canvas = props.refCanvas.value;

            if (canvas) {
                const ctx = canvas.getContext("2d");

                if (ctx) {
                    canvas.width            = canvas.offsetWidth * window.devicePixelRatio;
                    canvas.height           = canvas.offsetHeight * window.devicePixelRatio;
                    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
                    props.viewport.width    = canvas.offsetWidth;
                    props.viewport.height   = canvas.offsetHeight;
                    props.onRender();
                }
            }
        }

        let refPinchStartDistance = 0;
        let refInitialScale = props.refScale.value;
        let primaryTouchId: number | null = null;

        // Détecter le début du geste de pincement
        useEventListenerAutoSwitch(props.refCanvas, "touchstart", (e: TouchEvent) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            if (primaryTouchId === null) {
                const touch     = e.touches[0]!;
                const worldX    = (touch.clientX / props.refScale.value) + props.viewport.x;
                const worldY    = (touch.clientY / props.refScale.value) + props.viewport.y;

                primaryTouchId          = touch.identifier;
                refIsDragging.value     = true;
                refDragStart.value.x    = worldX;
                refDragStart.value.y    = worldY;

                props.onUpdate(worldX, worldY);
            }

            if (e.touches.length === 2) {
                // Calculer la distance initiale entre les deux doigts
                const dx = e.touches[1]!.clientX - e.touches[0]!.clientX;
                const dy = e.touches[1]!.clientY - e.touches[0]!.clientY;

                refPinchStartDistance   = Math.sqrt(dx * dx + dy * dy);
                refInitialScale         = props.refScale.value;
            }
        }, { passive: false });

        // Détecter le mouvement des doigts pour ajuster le zoom
        useEventListenerAutoSwitch(props.refCanvas, "touchmove", (e: TouchEvent) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            if (primaryTouchId !== null && refIsDragging.value) {
                if (e.touches.length === 2) {
                    // Calculer la nouvelle distance entre les deux doigts
                    const dx = e.touches[1]!.clientX - e.touches[0]!.clientX;
                    const dy = e.touches[1]!.clientY - e.touches[0]!.clientY;
                    const pinchDistance = Math.sqrt(dx * dx + dy * dy);

                    // Calculer le facteur de zoom basé sur la variation de distance
                    const scaleChange   = pinchDistance / refPinchStartDistance;
                    const newScale      = refInitialScale * scaleChange;

                    props.refScale.value = newScale;

                    // Ajuster la vue en conséquence
                    // props.viewport.x = refDragStart.value.x - ((refDragStart.value.x - props.viewport.x) / newScale);
                    // props.viewport.y = refDragStart.value.y - ((refDragStart.value.y - props.viewport.y) / newScale);
                    props.onRender();

                } else {
                    // Trouver le doigt principal dans la liste des touches
                    const primaryTouch = Array.from(e.touches).find((touch) => touch.identifier === primaryTouchId);

                    if (primaryTouch) {
                        if (refIsDragging.value) {
                            props.viewport.x = refDragStart.value.x - (primaryTouch.clientX / props.refScale.value);
                            props.viewport.y = refDragStart.value.y - (primaryTouch.clientY / props.refScale.value);
                            props.onRender();
                        }

                        const worldX = (primaryTouch.clientX / props.refScale.value) + props.viewport.x;
                        const worldY = (primaryTouch.clientY / props.refScale.value) + props.viewport.y;

                        props.onUpdate(worldX, worldY);
                    }
                }
            }
        }, { passive: false });

        // Réinitialiser après le geste
        useEventListenerAutoSwitch(props.refCanvas, "touchend", (e: TouchEvent) => {
            e.preventDefault();
            e.stopImmediatePropagation();

            if (primaryTouchId !== null) {
                // Vérifier si le doigt principal est relâché
                const remainingTouch = Array.from(e.touches).find(touch => touch.identifier === primaryTouchId);

                if (!remainingTouch) {
                    if (e.touches.length > 0) {
                        const newTouch = e.touches[0]!;

                        primaryTouchId          = newTouch.identifier;
                        refDragStart.value.x    = (newTouch.clientX / props.refScale.value) + props.viewport.x;
                        refDragStart.value.y    = (newTouch.clientY / props.refScale.value) + props.viewport.y;

                    } else {
                        // Si aucun doigt restant, réinitialiser
                        primaryTouchId = null;
                        refIsDragging.value = false;
                    }
                }

                if (e.touches.length === 2) {
                    // Calculer la distance initiale entre les deux doigts
                    const dx = e.touches[1]!.clientX - e.touches[0]!.clientX;
                    const dy = e.touches[1]!.clientY - e.touches[0]!.clientY;

                    refPinchStartDistance   = Math.sqrt(dx * dx + dy * dy);
                    refInitialScale         = props.refScale.value;
                }
            }
        }, { passive: false });

        useEventListener(window, "pointerup", (e: PointerEvent) => {
            if (e.pointerType !== "touch" && primaryTouchId === null)
                refIsDragging.value = false;
        }, { passive: true });

        useEventListenerAutoSwitch(props.refCanvas, "pointerdown", (e: PointerEvent) => {
            if (e.pointerType !== "touch" && primaryTouchId === null) {
                refIsDragging.value     = true;
                refDragStart.value.x    = (e.offsetX / props.refScale.value) + props.viewport.x;
                refDragStart.value.y    = (e.offsetY / props.refScale.value) + props.viewport.y;
            }
        }, { passive: true });

        useEventListenerAutoSwitch(props.refCanvas, "pointermove", (e: PointerEvent) => {
            if (e.pointerType !== "touch" && primaryTouchId === null) {
                if (refIsDragging.value) {
                    props.viewport.x = refDragStart.value.x - (e.offsetX / props.refScale.value);
                    props.viewport.y = refDragStart.value.y - (e.offsetY / props.refScale.value);
                    props.onRender();
                }

                const worldX = (e.offsetX / props.refScale.value) + props.viewport.x;
                const worldY = (e.offsetY / props.refScale.value) + props.viewport.y;

                props.onUpdate(worldX, worldY);
            }
        }, { passive: true });

        useEventListenerAutoSwitch(props.refCanvas, "wheel", (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const zoomIntensity     = 0.1; // Facteur de zoom
            const mouseX            = (e.offsetX / props.refScale.value) + props.viewport.x; // Position X de la souris dans le référentiel global
            const mouseY            = (e.offsetY / props.refScale.value) + props.viewport.y; // Position Y de la souris dans le référentiel global

            // Calcul de la nouvelle échelle
            const newScale = (e.deltaY < 0)
                ? props.refScale.value * (1 + zoomIntensity)
                : props.refScale.value / (1 + zoomIntensity);

            // Mise à jour des coordonnées du viewport pour maintenir l'objet sous la souris
            props.viewport.x        = mouseX - (e.offsetX / newScale);
            props.viewport.y        = mouseY - (e.offsetY / newScale);
            props.refScale.value    = newScale; // Mise à jour de l'échelle

            props.onRender(); // Rendu du canvas après le zoom
        }, { passive: false });

        useEventListener(window, "resize", updateCanvasSize);

        onMounted(() => {
            updateCanvasSize();
        });

        return () => <canvas ref={props.refCanvas} />;
    },
});
